import type { UIMessage } from '@ai-sdk/react';
import { ChatMessageUserFeedbackSchema } from '@bliks-io/contracts';
import type { ChatMessageMetadata, ChatTranscriptMessage } from '../ChatMessages';
import { isRenderablePart, type TranscriptPart } from '../parts';

/**
 * `UIMessage.metadata` is `unknown` by construction — it is whatever the
 * transport or the history converter attached — so every field is read
 * defensively and a shape that is not the expected one degrades to absent
 * rather than reaching the renderer as garbage.
 */
function readMetadata(metadata: unknown): ChatMessageMetadata {
  const candidate = metadata as ChatMessageMetadata | undefined;
  const refs = candidate?.entityRefs;
  const traceId = candidate?.langfuseTraceId;
  const provenance = candidate?.aiProvenance;
  return {
    entityRefs: Array.isArray(refs) ? refs : undefined,
    langfuseTraceId: typeof traceId === 'string' ? traceId : undefined,
    // The mark is a legal claim about the content, so only the shape the
    // contract defines (`aiGenerated` is a literal `true`) is honoured.
    aiProvenance: provenance?.aiGenerated === true ? provenance : undefined,
    // The verdict drives which thumb reads as pressed AND whether the row is
    // offered at all, so it is read through the contract's own union rather than
    // a hand-rolled restatement of it: anything else degrades to "not voted".
    userFeedback: ChatMessageUserFeedbackSchema.safeParse(candidate?.userFeedback).data
  };
}

/**
 * The transcript re-renders on every streamed token and `ChatMessage` is
 * memoised on its parts, so handing back a fresh array for a turn nothing was
 * filtered out of would defeat that memo and re-parse every message's markdown
 * per token. The original reference is kept whenever the filter changed nothing.
 */
function keepRenderableParts(parts: TranscriptPart[] | undefined): TranscriptPart[] | undefined {
  if (!parts) return undefined;
  const renderable = parts.filter(isRenderablePart);
  return renderable.length === parts.length ? parts : renderable;
}

/**
 * Convert UIMessage[] (AI SDK format) to the transcript's own message shape:
 * the parts the renderer understands — which kinds those are is owned by
 * `../parts`, not decided here — plus the message-level metadata carried
 * through untouched.
 */
export function convertToMessageList(messages: UIMessage[]): ChatTranscriptMessage[] {
  return messages.map((m) => ({
    id: m.id,
    role: m.role,
    parts: keepRenderableParts(m.parts),
    metadata: readMetadata(m.metadata)
  }));
}
